import { Autocomplete, Menu, MenuItem, TextField } from "@mui/material";
import * as numbers from "common/numbers";
import Disabled from "components/common/Disabled";
import * as labels from "config.labels";
import { TOWER_PRICING_SIR_COPY_ENABLED } from "flags";
import { useSnackbar } from "notistack";
import React, { useMemo, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import * as staticDataService from "services/staticDataService";
import * as towerPricingActions from "store/actions/pricing/tower/towerPricingActions";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import { useIsVisible } from "store/hooks/staticDataHooks";
import * as claimsSelectors from "store/selectors/input/claims/claimsSelectors";

const TowerLayerIncludeSir = ({ layer, layerIndex }) => {
  const dispatch = useDispatch();
  const store = useStore();
  const { enqueueSnackbar } = useSnackbar();

  const sirs = useSelector(claimsSelectors.selectSelfInsuredRetentions) || [];

  const lossCategories = useSelector(
    (state) => state?.temp?.exposureMetrics?.lossCategories || []
  );
  const lossTypeMap = useMemo(() => {
    return Object.fromEntries(lossCategories.map((c) => [c.key, c.display]));
  }, [lossCategories]);

  const { data: retentionsConfig } = staticDataService.useConfigQuery(
    "retentions"
  );
  const typeMap = useMemo(() => {
    const entries = (retentionsConfig?.retentionTypes || []).map((t) => [
      t.key,
      t.display,
    ]);
    return Object.fromEntries(entries);
  }, [retentionsConfig?.retentionTypes]);
  const coverageMap = useMemo(() => {
    const entries = (
      retentionsConfig?.retentionCoverageCategory || []
    ).map((c) => [c.key, c.display]);
    return Object.fromEntries(entries);
  }, [retentionsConfig?.retentionCoverageCategory]);

  const includeSir = layer?.includeSir ?? null;

  const setIncludeSir = (id) => {
    dispatch(towerPricingActions.updateLayer(layerIndex, { includeSir: id }));
  };

  // Context menu state and feature flag
  const { isVisible: copyMenuVisible } = useIsVisible(
    TOWER_PRICING_SIR_COPY_ENABLED
  );
  const [contextMenu, setContextMenu] = useState(null);
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const applyToAllLabel = translateLabel(labels.TOWER_SIR_COPY_TO_ALL_LABEL, {
    default: "Apply SIR to All Layers",
  });
  const applyToEmptyLabel = translateLabel(
    labels.TOWER_SIR_COPY_TO_EMPTY_LABEL,
    { default: "Apply SIR to Empty Layers" }
  );

  const handleContextMenu = (e) => {
    if (copyMenuVisible && includeSir != null) {
      e.preventDefault();
      setContextMenu({ mouseX: e.clientX + 2, mouseY: e.clientY - 6 });
    }
  };

  const handleApply = (applyAll) => {
    setContextMenu(null);
    const layers = store.getState().pricing?.tower?.layers ?? [];
    const targetCount = applyAll
      ? layers.length
      : layers.filter((l) => l.includeSir == null).length;
    dispatch(
      towerPricingActions.updateAllLayersIncludeSir(includeSir, applyAll)
    );
    enqueueSnackbar(
      `${targetCount} of ${layers.length} layer${
        layers.length !== 1 ? "s" : ""
      } updated`,
      { autoHideDuration: 3000 }
    );
  };

  return (
    <div onContextMenu={handleContextMenu}>
      <Disabled ifReadOnly>
        <Autocomplete
          value={sirs.find((sir) => sir.id === includeSir) ?? null}
          onChange={(_, value) => setIncludeSir(value?.id ?? null)}
          options={sirs}
          isOptionEqualToValue={(a, b) => a?.id === b?.id}
          getOptionLabel={(option) => {
            const valuePart = numbers.abbreviated(option?.value);
            const retentionTypeKey = option?.retentionType;
            const tokens = [];

            if (retentionTypeKey === "SIMPLE") {
              if (option?.lossType) {
                tokens.push(lossTypeMap[option.lossType] || option.lossType);
              }
            } else {
              if (retentionTypeKey) {
                tokens.push(typeMap[retentionTypeKey] || retentionTypeKey);
              }
              if (option?.retentionCoverageCategory) {
                tokens.push(
                  coverageMap[option.retentionCoverageCategory] ||
                    option.retentionCoverageCategory
                );
              }
            }

            return tokens.length
              ? `${valuePart} - ${tokens.join(" - ")}`
              : `${valuePart}`;
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant={"filled"}
              size={"small"}
              hiddenLabel
            />
          )}
          autoHighlight
          size={"small"}
        />
      </Disabled>
      <Menu
        open={contextMenu != null}
        onClose={() => setContextMenu(null)}
        anchorReference={"anchorPosition"}
        anchorPosition={
          contextMenu
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={() => handleApply(true)}>{applyToAllLabel}</MenuItem>
        <MenuItem onClick={() => handleApply(false)}>
          {applyToEmptyLabel}
        </MenuItem>
      </Menu>
    </div>
  );
};

export default TowerLayerIncludeSir;
